"use client";

import { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const POLL_INTERVAL_MS = 4000;
// Nach 15 Minuten aufhoeren zu pollen (Nutzer hat die Mail vermutlich nicht mehr offen) -
// verhindert eine Dauerschleife bei verlassenen Tabs.
const POLL_MAX_ATTEMPTS = (15 * 60 * 1000) / POLL_INTERVAL_MS;

export function EmailGateModal() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "sent" | "error">("idle");
  const [token, setToken] = useState<string | null>(null);
  const pollAttemptsRef = useRef(0);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setStatus("loading");
    try {
      const res = await fetch("/api/leadgate/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (!res.ok) {
        setStatus("error");
        return;
      }
      const data = (await res.json()) as { token?: string };
      pollAttemptsRef.current = 0;
      setToken(data.token ?? null);
      setStatus("sent");
    } catch {
      setStatus("error");
    }
  }

  // Pollt, ob der Bestaetigungslink inzwischen bestaetigt wurde - faengt den Fall ab, dass
  // der Link auf einem ANDEREN Geraet geklickt wird (z. B. Handy statt Laptop), wo dieses
  // Fenster sonst ewig auf "sent" haengen bliebe, weil das ek_verified-Cookie nur auf dem
  // klickenden Geraet gesetzt wird (siehe /api/leadgate/verify).
  useEffect(() => {
    if (status !== "sent" || !token) return;

    const interval = setInterval(async () => {
      pollAttemptsRef.current += 1;
      if (pollAttemptsRef.current > POLL_MAX_ATTEMPTS) {
        clearInterval(interval);
        return;
      }
      try {
        const res = await fetch(`/api/leadgate/status?token=${encodeURIComponent(token)}`);
        if (!res.ok) return;
        const data = (await res.json()) as { verified: boolean };
        if (data.verified) {
          clearInterval(interval);
          document.cookie = `ek_verified=1; path=/; max-age=${60 * 60 * 24 * 365}; samesite=lax`;
          window.location.reload();
        }
      } catch {
        // Netzwerkfehler beim Polling: naechster Versuch in POLL_INTERVAL_MS, kein Abbruch
      }
    }, POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [status, token]);

  return (
    <Dialog open modal>
      <DialogContent
        showCloseButton={false}
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        className="sm:max-w-md"
      >
        <DialogHeader>
          <DialogTitle className="font-serif text-xl">
            Weiter geht&apos;s mit deiner E-Mail-Adresse
          </DialogTitle>
          <DialogDescription>
            Du hast schon einige Aktien mit Erikonomie analysiert. Trage deine
            E-Mail-Adresse ein, um kostenlos weiterzusuchen — wir schicken dir dafür
            einmalig einen Bestätigungslink.
          </DialogDescription>
        </DialogHeader>
        {status === "sent" ? (
          <p className="text-sm text-ink-secondary">
            Fast geschafft! Bitte bestätige deine E-Mail-Adresse über den Link, den wir
            dir gerade geschickt haben. Diese Seite geht automatisch weiter, sobald du den
            Link bestätigt hast — auch wenn du ihn auf einem anderen Gerät öffnest.
          </p>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col gap-3">
            <Input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="deine@email.de"
              aria-label="E-Mail-Adresse"
              className="bg-card"
            />
            <Button type="submit" disabled={status === "loading"}>
              {status === "loading" ? "Wird gesendet…" : "Bestätigungslink senden"}
            </Button>
            {status === "error" && (
              <p className="text-xs text-negative">
                Das hat gerade nicht geklappt. Bitte später erneut versuchen.
              </p>
            )}
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
